import { useEffect, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import "./style.css";
import { inventoryData } from "./data/inventory-data";
import { addAllWidgetsScripts, addGoogleAnalyticsScript } from "./helpers";
import { getConfig } from "@testing-library/react";

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  font-family: Arial, sans-serif;
  display: block;
  overflow-y: auto;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const Title = styled.h1`
  color: #333;
`;

const BackButton = styled.button`
  background-color: #2c6b9c;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;

  &:hover {
    background-color: #1e5380;
  }
`;

const CarDetailLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CarImageSection = styled.div`
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
`;

const CarImage = styled.div`
  height: 300px;
  background-color: #f5f5f5;
  background-image: url(${(props) => props.src});
  background-size: cover;
  background-position: center;
`;

const CarInfoSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const CarTitle = styled.h2`
  margin: 0 0 15px 0;
  color: #333;
  font-size: 28px;
`;

const CarPrice = styled.div`
  font-weight: bold;
  font-size: 24px;
  color: #2c6b9c;
  margin-bottom: 20px;
`;

const CarSpecs = styled.div`
  background-color: #f9f9f9;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
`;

const SpecRow = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #eee;
  padding: 10px 0;

  &:last-child {
    border-bottom: none;
  }
`;

const SpecLabel = styled.span`
  color: #666;
  font-weight: bold;
`;

const SpecValue = styled.span`
  color: #333;
`;

const ActionButton = styled.button`
  background-color: #2c6b9c;
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 4px;
  font-weight: bold;
  margin-top: 10px;
  cursor: pointer;

  &:hover {
    background-color: #1e5380;
  }
`;

const Description = styled.div`
  margin-top: 30px;
  line-height: 1.6;
`;

const { locationId } = getConfig();

const Details = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [car, setCar] = useState(null);

  useEffect(() => {
    if (location.state?.car) {
      // Car data passed through navigation
      setCar(location.state.car);
    } else {
      // Try to find car by VIN from URL
      const vinFromUrl = searchParams.get("vin");
      if (vinFromUrl) {
        const foundCar = inventoryData.find((car) => car.vin === vinFromUrl);
        if (foundCar) {
          setCar(foundCar);
        } else {
          navigate("/demo-inventory");
        }
      } else {
        navigate("/demo-inventory");
      }
    }
  }, [location, navigate, searchParams]);

  useEffect(() => {
    addGoogleAnalyticsScript();
    addAllWidgetsScripts();
  }, []);

  useEffect(() => {
    if (car) {
      // Always update or create the structured data script when car changes
      const existingScript = document.getElementById("vehicle-ld-json");

      // Create the JSON-LD data structure
      const jsonLdData = {
        "@context": "http://schema.org",
        "@type": "Car",
        itemCondition: "UsedCondition",
        name: `${car.year} ${car.make} ${car.model} ${car.trim}`,
        modelDate: car.year,
        vehicleModelDate: car.year,
        manufacturer: car.make,
        model: car.model,
        bodyType: car.type.charAt(0).toUpperCase() + car.type.slice(1),
        color: car.color || "",
        vehicleTransmission: car.transmission || "",
        image: car.imageUrl || "",
        brand: { "@type": "Brand", name: car.make },
        description: `${car.year} ${car.make} ${car.model} ${
          car.trim
        } with ${car.mileage.toLocaleString()} miles.`,
        mileageFromOdometer: { value: car.mileage, unitCode: "SMI" },
        sku: car.stock,
        vehicleIdentificationNumber: car.vin,
        offers: {
          "@type": "Offer",
          price: car.price,
          priceCurrency: "USD",
          itemCondition: "http://schema.org/UsedCondition",
          availability: "http://schema.org/InStock",
          url: window.location.href,
        },
      };

      if (existingScript) {
        // Update existing script
        existingScript.textContent = JSON.stringify(jsonLdData);
      } else {
        // Create new script
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = "vehicle-ld-json";
        script.textContent = JSON.stringify(jsonLdData);
        document.body.appendChild(script);
      }
    }

    return () => {
      const script = document.getElementById("vehicle-ld-json");
      if (script) {
        document.body.removeChild(script);
      }
    };
  }, [car]);

  if (!car) {
    return <Container>Loading...</Container>;
  }

  return (
    <Container>
      <Header>
        <Title>Matador Motors</Title>
        <BackButton onClick={() => navigate("/demo-inventory")}>
          Back to Inventory
        </BackButton>
      </Header>

      <CarDetailLayout>
        <CarImageSection>
          <CarImage src={car.imageUrl || ""} />
        </CarImageSection>

        <CarInfoSection>
          <CarTitle>
            {car.year} {car.make} {car.model}
          </CarTitle>
          <CarPrice>${car.price.toLocaleString()}</CarPrice>

          <CarSpecs>
            <SpecRow>
              <SpecLabel>Year:</SpecLabel>
              <SpecValue>{car.year}</SpecValue>
            </SpecRow>
            <SpecRow>
              <SpecLabel>Make:</SpecLabel>
              <SpecValue>{car.make}</SpecValue>
            </SpecRow>
            <SpecRow>
              <SpecLabel>Model:</SpecLabel>
              <SpecValue>{car.model}</SpecValue>
            </SpecRow>
            <SpecRow>
              <SpecLabel>Trim:</SpecLabel>
              <SpecValue>{car.trim}</SpecValue>
            </SpecRow>
            <SpecRow>
              <SpecLabel>VIN:</SpecLabel>
              <SpecValue>{car.vin}</SpecValue>
            </SpecRow>
            <SpecRow>
              <SpecLabel>Stock Number:</SpecLabel>
              <SpecValue>{car.stock}</SpecValue>
            </SpecRow>
            <SpecRow>
              <SpecLabel>Mileage:</SpecLabel>
              <SpecValue>{car.mileage.toLocaleString()} miles</SpecValue>
            </SpecRow>
            <SpecRow>
              <SpecLabel>Body Style:</SpecLabel>
              <SpecValue>
                {car.type.charAt(0).toUpperCase() + car.type.slice(1)}
              </SpecValue>
            </SpecRow>
            {car.color && (
              <SpecRow>
                <SpecLabel>Color:</SpecLabel>
                <SpecValue>{car.color}</SpecValue>
              </SpecRow>
            )}
            {car.transmission && (
              <SpecRow>
                <SpecLabel>Transmission:</SpecLabel>
                <SpecValue>{car.transmission}</SpecValue>
              </SpecRow>
            )}
            {car.fuelType && (
              <SpecRow>
                <SpecLabel>Fuel Type:</SpecLabel>
                <SpecValue>{car.fuelType}</SpecValue>
              </SpecRow>
            )}
            {car.engine && (
              <SpecRow>
                <SpecLabel>Engine:</SpecLabel>
                <SpecValue>{car.engine}</SpecValue>
              </SpecRow>
            )}
          </CarSpecs>

          <div className="matador-widget" data-id={locationId}></div>

          <ActionButton>Schedule Test Drive</ActionButton>
          <ActionButton style={{ backgroundColor: "#4CAF50" }}>
            Contact Us About This Vehicle
          </ActionButton>
        </CarInfoSection>
      </CarDetailLayout>

      <Description>
        <h3>Vehicle Description</h3>
        <p>
          This {car.year} {car.make} {car.model} {car.trim} is available with{" "}
          {car.mileage.toLocaleString()} miles. It comes with stock number{" "}
          {car.stock} and VIN {car.vin}.
        </p>

        {car.features && car.features.length > 0 && (
          <>
            <h4>Features</h4>
            <ul>
              {car.features.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
          </>
        )}
      </Description>
    </Container>
  );
};

export default Details;
